import { Button, InfoPage } from "@shapr3d/frontend-lib";
import type { MutationStatus } from "@tanstack/react-query";

import useTranslation from "../../hooks/useTranslation";
import Heading from "../Heading/Heading";

import style from "./AppAuth.module.css";

interface AppAuthProps {
  onContinueClick: () => void;
  onLoginClick: () => void;
  onLaunchAppClick: () => void;
  onBackToLoginClick: () => void;
  authState: MutationStatus;
  isSurveySuccess: boolean;
  email: string;
  hasError?: boolean;
  isExpired?: boolean;
}

const AppAuth = (props: AppAuthProps) => {
  let content = null;
  const { t, m } = useTranslation();

  if (props.hasError || props.authState === "error") {
    content = <div className={style.text}>{t(m.AppAuth.SomethingWentWrong)}</div>;
  } else if (props.isExpired) {
    content = (
      <>
        <Heading type="h1">{t(m.AppAuth.LoginExpired)}</Heading>
        <div className={style.text}>{t(m.AppAuth.TryLoginAgain)}</div>
        <Button onClick={props.onBackToLoginClick}>{t(m.AppAuth.BackToLogin)}</Button>
      </>
    );
  } else if (props.isSurveySuccess) {
    return (
      <InfoPage
        title={t(m.AppAuth.SurveySuccess.Title)}
        description={t(m.AppAuth.SurveySuccess.SubTitle)}
        primaryCTA={<RedirectButton onLaunchAppClick={props.onLaunchAppClick} />}
      />
    );
  } else if (props.authState === "success") {
    content = (
      <>
        <div className={style.text}>
          {t(m.AppAuth.OpenInstructions, {
            openShapr: <strong>{t(m.AppAuth.OpenShapr)}</strong>,
          })}
        </div>
        <div className={style.text}>
          {t(m.AppAuth.IfYouDontSee, {
            launchShapr: <strong>{t(m.AppAuth.LaunchShapr)}</strong>,
          })}
        </div>
        <RedirectButton onLaunchAppClick={props.onLaunchAppClick} />
      </>
    );
  } else {
    const isPending = props.authState === "pending";
    content = (
      <>
        <div className={style.text}>
          <span>{t(m.AppAuth.LoggedAs)}</span>
          <div className={style.break}>
            <strong>{props.email}</strong>
          </div>
        </div>
        <Button onClick={props.onContinueClick} disabled={isPending} aria-label="continue">
          {t(m.General.Continue)}
        </Button>
        <Button
          onClick={props.onLoginClick}
          variant="grayscale"
          disabled={isPending}
          aria-label="login different"
        >
          {t(m.AppAuth.LoginWithDifferentAccount)}
        </Button>
      </>
    );
  }

  return (
    <div className={style.appAuth}>
      <div className={style.appAuthForm}>
        {!props.isExpired && <Heading type="h1">{t(m.General.Login)}</Heading>}
        {content}
      </div>
    </div>
  );
};

interface RedirectButtonProps {
  onLaunchAppClick: () => void;
}

const RedirectButton: React.FC<RedirectButtonProps> = ({ onLaunchAppClick }) => {
  const { t, m } = useTranslation();

  return <Button onClick={onLaunchAppClick}>{t(m.AppAuth.LaunchShapr)}</Button>;
};

export default AppAuth;
